import { useState, useMemo } from 'react';
import { usePersistedState } from '@/hooks/usePersistedState';
import { ArrowLeft, ArrowUpDown, MessageSquare } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { PatientListItem, ListResult } from '../../types/analytics.types';
import { formatDate, formatPhoneNumber } from '../../lib/formatters';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { BulkActionsBar } from './BulkActionsBar';
import { PatientDetailPanel } from './PatientDetailPanel';

interface PatientListViewProps {
  data: ListResult;
}

export function PatientListView({ data }: PatientListViewProps) {
  const [sorting, setSorting] = usePersistedState<SortingState>('view:analytics-patient-list:sorting', []);
  const [selectedPatient, setSelectedPatient] = useState<PatientListItem | null>(null);

  const {
    selectedPatientIds,
    togglePatientSelection,
    selectAllPatients,
    clearPatientSelection,
    setUIState,
  } = useAnalyticsStore();

  const handleBack = () => {
    setUIState('idle');
  };

  const columns = useMemo<ColumnDef<PatientListItem>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => {
              if (value) {
                selectAllPatients(data.patients.map((p) => p.id));
              } else {
                clearPatientSelection();
              }
            }}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedPatientIds.includes(row.original.id)}
            onCheckedChange={() => togglePatientSelection(row.original.id)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="-ml-4"
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div>
            <div className="font-medium">
              {row.original.firstName} {row.original.lastName}
            </div>
            <div className="text-xs text-muted-foreground">
              MRN: {row.original.mrn}
            </div>
          </div>
        ),
        sortingFn: (rowA, rowB) => {
          const nameA = `${rowA.original.lastName} ${rowA.original.firstName}`;
          const nameB = `${rowB.original.lastName} ${rowB.original.firstName}`;
          return nameA.localeCompare(nameB);
        },
      },
      {
        accessorKey: 'age',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="-ml-4"
          >
            Age/DOB
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div>
            <div>{row.original.age} yrs</div>
            <div className="text-xs text-muted-foreground">
              {formatDate(row.original.dateOfBirth)}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'pcp',
        header: 'PCP',
        cell: ({ row }) => row.original.pcp || '-',
      },
      {
        accessorKey: 'careManager',
        header: 'Care Manager',
        cell: ({ row }) => row.original.careManager || '-',
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span>{row.original.phone ? formatPhoneNumber(row.original.phone) : '-'}</span>
            {row.original.smsCapable && (
              <Badge variant="secondary" className="bg-success/10 text-success">
                <MessageSquare className="h-3 w-3" />
              </Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'riskScore',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="-ml-4"
          >
            Risk
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const score = row.original.riskScore;
          if (!score) return '-';

          let colorClass = 'bg-success/10 text-success';
          if (score >= 3) colorClass = 'bg-destructive/10 text-destructive';
          else if (score >= 2) colorClass = 'bg-warning/10 text-warning';

          return <Badge className={colorClass}>{score.toFixed(1)}</Badge>;
        },
      },
    ],
    [selectedPatientIds, togglePatientSelection, selectAllPatients, clearPatientSelection, data.patients]
  );

  const table = useReactTable({
    data: data.patients,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-lg font-semibold">
            Patient List
            <span className="text-muted-foreground font-normal ml-2">
              ({data.totalCount.toLocaleString()} patients)
            </span>
          </h3>
        </div>
      </div>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedPatient(row.original)}
                  data-state={selectedPatientIds.includes(row.original.id) ? 'selected' : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} onClick={(e) => {
                      if (cell.column.id === 'select') {
                        e.stopPropagation();
                      }
                    }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No patients found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <BulkActionsBar selectedCount={selectedPatientIds.length} />

      <PatientDetailPanel
        patient={selectedPatient}
        open={!!selectedPatient}
        onClose={() => setSelectedPatient(null)}
      />
    </div>
  );
}
